/**
 * SplitPanelContainer - Container managing ChatPanel + SummaryPanel
 *
 * Provides side-by-side layout for:
 * - Left: Full chat conversation for browsing/searching
 * - Right: Live summary view showing collected items (cart, shortlist)
 *
 * Features:
 * - Auto-opens summary panel when first item is added (configurable)
 * - Responsive: side-by-side on desktop, slide-over on mobile
 * - Toggle button to show/hide summary panel
 * - Shares state via SharedListStore
 */

import type { WidgetConfig } from '../core/config';
import { ChatPanel, type PanelCallbacks } from './panel';
import { SummaryPanel, type SummaryAction } from './summary-panel';
import { getListStore, resetListStore, type ListItem } from '../core/list-store';
import { addToCart, detectPlatform, type CartResult } from '../core/cart-integration';
import { warn } from '../core/logger';
import type { HostPagePusher } from '../core/host-page-pusher';

export interface SplitPanelConfig {
  /** Enable split panel feature */
  enabled: boolean;
  /** Auto-open summary when first item added */
  autoOpen?: boolean;
  /** Default actions shown in summary panel */
  summaryActions?: SummaryAction[];
  /** Title for summary panel */
  summaryTitle?: string;
  /** Empty state message */
  emptyMessage?: string;
}

export interface SplitPanelCallbacks extends PanelCallbacks {
  /** Called when summary panel action is triggered */
  onSummaryAction?: (action: string, items: ListItem[]) => void;
}

/**
 * Split panel container class
 */
export class SplitPanelContainer {
  private config: WidgetConfig;
  private splitConfig: SplitPanelConfig;
  private callbacks: SplitPanelCallbacks;

  private wrapper: HTMLElement | null = null;
  private container: HTMLElement | null = null;
  private chatPanel: ChatPanel | null = null;
  private summaryPanel: SummaryPanel | null = null;
  private summaryContainer: HTMLElement | null = null;
  private toggleButton: HTMLButtonElement | null = null;
  private topHandle: HTMLElement | null = null;
  private minimizeButton: HTMLButtonElement | null = null;
  private menuButton: HTMLButtonElement | null = null;
  private menuDropdown: HTMLElement | null = null;
  private floatingBubble: HTMLButtonElement | null = null;
  private boundMenuDocClickHandler: (() => void) | null = null;
  private layoutToggleBtn: HTMLButtonElement | null = null;
  private floatingLayoutToggle: HTMLButtonElement | null = null;
  private activeLayout: 'immersive' | 'vertical' = 'immersive';

  // Vertical resize state (left-edge width drag)
  private verticalResizeHandle: HTMLElement | null = null;
  private isResizingLeft = false;
  private resizeStartX = 0;
  private resizeStartWidth = 0;
  private boundLeftMouseMove: ((e: MouseEvent) => void) | null = null;
  private boundLeftMouseUp: (() => void) | null = null;
  private boundLeftTouchMove: ((e: TouchEvent) => void) | null = null;
  private boundLeftTouchEnd: (() => void) | null = null;

  private pusher: HostPagePusher | null = null;
  private isSummaryOpen: boolean = false;
  private isMinimized: boolean = false;
  private unsubscribeFromList: (() => void) | null = null;

  // Top handle resize state - calculated based on initialHeight config
  private containerTop: number = 0; // Set in constructor based on config
  private isResizingTop = false;
  private resizeStartY = 0;
  private resizeStartTop = 0;
  private boundTopMouseMove: ((e: MouseEvent) => void) | null = null;
  private boundTopMouseUp: (() => void) | null = null;
  private boundTopTouchMove: ((e: TouchEvent) => void) | null = null;
  private boundTopTouchEnd: (() => void) | null = null;

  constructor(
    config: WidgetConfig,
    callbacks: SplitPanelCallbacks,
    splitConfig: SplitPanelConfig = { enabled: true }
  ) {
    this.config = config;
    this.callbacks = callbacks;
    this.splitConfig = {
      autoOpen: true,
      summaryTitle: 'Your List',
      emptyMessage: 'Add items from the chat to see them here',
      summaryActions: [
        { label: 'Compare', value: 'compare', minItems: 2 },
        { label: 'Continue', value: 'checkout', variant: 'primary', minItems: 1 },
      ],
      ...splitConfig,  // Will override with provided config including 'enabled'
    };

    // Calculate initial container position based on initialHeight
    // initialHeight is panel height as percentage (e.g., 30 = 30% panel, 70% site visible)
    // Default to 65% panel height (35% from top)
    const initialHeight = config.splitPanel?.initialHeight ?? 65;
    const topPercent = (100 - initialHeight) / 100;
    this.containerTop = Math.round(window.innerHeight * topPercent);

    // Restore saved position from sessionStorage (preserves position across workspace switches)
    const savedTop = sessionStorage.getItem('pms-panel-top');
    if (savedTop) {
      const parsedTop = parseInt(savedTop, 10);
      // Validate the saved position is still reasonable
      const minTop = 0;
      const maxTop = window.innerHeight - 10; // Leave 10px grab area
      if (!isNaN(parsedTop) && parsedTop >= minTop && parsedTop <= maxTop) {
        this.containerTop = parsedTop;
      }
    }
  }

  /**
   * Create the split panel container
   */
  create(): HTMLElement {
    // Create wrapper for overlay positioning
    this.wrapper = document.createElement('div');
    this.wrapper.className = 'pms-split-wrapper';

    // Create main container
    this.container = document.createElement('div');
    this.container.className = 'pms-split-container';

    // Add immersive/vertical class for proper styling
    if (this.config.style === 'immersive' || this.config.style === 'vertical') {
      this.container.classList.add(this.config.style === 'vertical' ? 'pms-split-vertical' : 'pms-split-immersive');
    }

    // Always create top handle (needed for runtime style switching bubble → immersive)
    this.createTopHandle();
    if (this.config.style === 'immersive') {
      this.updateContainerTop();
    }

    // Create chat panel wrapper
    const chatWrapper = document.createElement('div');
    chatWrapper.className = 'pms-split-chat';

    // Create the chat panel with wrapped callbacks
    const wrappedCallbacks: PanelCallbacks = {
      ...this.callbacks,
      onActionClick: (action) => {
        this.handleChatAction(action);
        this.callbacks.onActionClick?.(action);
      },
    };

    this.chatPanel = new ChatPanel(this.config, wrappedCallbacks);
    const chatElement = this.chatPanel.create();
    chatWrapper.appendChild(chatElement);

    this.container.appendChild(chatWrapper);

    // Note: In split immersive mode, the container top handle (created in createTopHandle())
    // is used for drag resize. The panel header is hidden via CSS.

    // Summary panel + toggle button disabled — feature not ready for users yet
    // if (this.splitConfig.enabled) {
    //   this.createSummaryPanel();
    //   this.createToggleButton();
    //   this.subscribeToListChanges();
    // }

    // Always create menu, layout toggle, bubble switch, minimize and floating bubble
    // (needed for runtime style switching bubble ↔ immersive)
    this.createMenuButton();
    this.createLayoutToggleButton();
    this.createBubbleSwitchButton();
    this.createMinimizeButton();
    this.createFloatingBubble();

    // Add container to wrapper
    this.wrapper.appendChild(this.container);

    return this.wrapper;
  }

  /**
   * Create top handle for container resize with branding
   */
  private createTopHandle(): void {
    if (!this.container) return;

    this.topHandle = document.createElement('div');
    this.topHandle.className = 'pms-container-top-handle';

    // Add branding section
    const branding = document.createElement('div');
    branding.className = 'pms-top-handle-branding';

    // Add logo if available
    const logoUrl = this.config.branding?.logoUrl || this.config.theme?.headerLogo;
    if (logoUrl) {
      const logo = document.createElement('img');
      logo.className = 'pms-top-handle-logo';
      logo.src = logoUrl;
      logo.alt = this.config.branding?.logoAlt || '';
      branding.appendChild(logo);
    }

    // Add company name
    const companyName = this.config.branding?.companyName;
    if (companyName) {
      const name = document.createElement('span');
      name.className = 'pms-top-handle-name';
      name.textContent = companyName;
      branding.appendChild(name);
    }

    this.topHandle.appendChild(branding);

    // Add drag bar indicator (in center)
    const dragBar = document.createElement('div');
    dragBar.className = 'pms-container-top-handle-bar';
    this.topHandle.appendChild(dragBar);

    // Add buttons container (right side) - buttons will be added later
    const buttonsContainer = document.createElement('div');
    buttonsContainer.className = 'pms-top-handle-buttons';
    this.topHandle.appendChild(buttonsContainer);

    // Insert at the beginning of the container
    this.container.insertBefore(this.topHandle, this.container.firstChild);

    // Setup appropriate resize based on initial style
    if (this.config.style === 'vertical') {
      this.topHandle.style.cursor = 'default';
      // Vertical resize (left-edge) set up after panel is created — deferred to createLayoutToggleButton
    } else {
      this.setupTopHandleResize();
    }
  }

  /**
   * Setup drag events for top handle
   */
  private setupTopHandleResize(): void {
    if (!this.topHandle) return;

    const minTop = 0; // Allow dragging to top of screen
    const maxTop = window.innerHeight - 10; // Leave 10px grab area at bottom

    // Bound handlers for cleanup
    this.boundTopMouseMove = (e: MouseEvent) => {
      if (!this.isResizingTop) return;
      e.preventDefault();

      const delta = e.clientY - this.resizeStartY;
      let newTop = this.resizeStartTop + delta;
      newTop = Math.max(minTop, Math.min(maxTop, newTop));

      this.containerTop = newTop;
      this.updateContainerTop();
      this.pusher?.updateDuringDrag(window.innerHeight - newTop);
    };

    this.boundTopMouseUp = () => {
      this.isResizingTop = false;
      this.container?.classList.remove('pms-resizing');
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      this.pusher?.endDrag();
      if (this.boundTopMouseMove) {
        document.removeEventListener('mousemove', this.boundTopMouseMove);
      }
      if (this.boundTopMouseUp) {
        document.removeEventListener('mouseup', this.boundTopMouseUp);
      }
    };

    this.boundTopTouchMove = (e: TouchEvent) => {
      if (!this.isResizingTop || e.touches.length !== 1) return;
      e.preventDefault();

      const touch = e.touches[0]!;
      const delta = touch.clientY - this.resizeStartY;
      let newTop = this.resizeStartTop + delta;
      newTop = Math.max(0, Math.min(maxTop, newTop));

      this.containerTop = newTop;
      this.updateContainerTop();
      this.pusher?.updateDuringDrag(window.innerHeight - newTop);
    };

    this.boundTopTouchEnd = () => {
      this.isResizingTop = false;
      this.container?.classList.remove('pms-resizing');
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      this.pusher?.endDrag();
      if (this.boundTopTouchMove) {
        document.removeEventListener('touchmove', this.boundTopTouchMove);
      }
      if (this.boundTopTouchEnd) {
        document.removeEventListener('touchend', this.boundTopTouchEnd);
        document.removeEventListener('touchcancel', this.boundTopTouchEnd);
      }
    };

    const startResize = (clientY: number) => {
      this.isResizingTop = true;
      this.resizeStartY = clientY;
      this.resizeStartTop = this.containerTop;

      this.container?.classList.add('pms-resizing');
      document.body.style.cursor = 'ns-resize';
      document.body.style.userSelect = 'none';
    };

    // Mouse events
    this.topHandle.addEventListener('mousedown', (e: MouseEvent) => {
      e.preventDefault();
      startResize(e.clientY);
      document.addEventListener('mousemove', this.boundTopMouseMove!);
      document.addEventListener('mouseup', this.boundTopMouseUp!);
    });

    // Touch events
    this.topHandle.addEventListener(
      'touchstart',
      (e: TouchEvent) => {
        if (e.touches.length !== 1) return;
        e.preventDefault();
        startResize(e.touches[0]!.clientY);
        document.addEventListener('touchmove', this.boundTopTouchMove!, { passive: false });
        document.addEventListener('touchend', this.boundTopTouchEnd!);
        document.addEventListener('touchcancel', this.boundTopTouchEnd!);
      },
      { passive: false }
    );
  }

  /**
   * Update container top position
   */
  private updateContainerTop(): void {
    if (!this.container) return;
    this.container.style.setProperty('--container-top', `${this.containerTop}px`);
  }

  /**
   * Create the summary panel (disabled — feature not ready)
   */
  // @ts-ignore: Kept for re-enablement when list sidebar is ready
  private createSummaryPanel(): void {
    if (!this.container) return;

    this.summaryContainer = document.createElement('div');
    this.summaryContainer.className = 'pms-split-summary';

    this.summaryPanel = new SummaryPanel(this.summaryContainer, {
      title: this.splitConfig.summaryTitle,
      emptyMessage: this.splitConfig.emptyMessage,
      showTotal: true,
      actions: this.splitConfig.summaryActions,
      onAction: (action, items) => {
        this.handleSummaryAction(action, items);
      },
      onClose: () => {
        this.closeSummary();
      },
    });

    this.container.appendChild(this.summaryContainer);
  }

  /**
   * Create the toggle button for summary panel (disabled — feature not ready)
   */
  // @ts-ignore: Kept for re-enablement when list sidebar is ready
  private createToggleButton(): void {
    if (!this.container) return;

    this.toggleButton = document.createElement('button');
    this.toggleButton.className = 'pms-split-toggle';
    this.toggleButton.title = 'Toggle list view';
    this.toggleButton.innerHTML = `
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="20" height="20">
        <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/>
        <line x1="3" y1="6" x2="21" y2="6"/>
        <path d="M16 10a4 4 0 0 1-8 0"/>
      </svg>
      <span class="pms-split-toggle-badge">0</span>
    `;

    this.toggleButton.addEventListener('click', (e) => {
      e.stopPropagation(); // Prevent triggering resize
      this.toggleSummary();
    });

    // In split immersive mode, add to top handle buttons container
    const topHandleButtons = this.topHandle?.querySelector('.pms-top-handle-buttons');
    if (topHandleButtons) {
      // Insert before minimize button if exists
      const minimizeBtn = topHandleButtons.querySelector('.pms-minimize-btn');
      if (minimizeBtn) {
        topHandleButtons.insertBefore(this.toggleButton, minimizeBtn);
      } else {
        topHandleButtons.appendChild(this.toggleButton);
      }
    } else {
      // Fallback to panel header (non-split mode)
      const chatHeader = this.container.querySelector('.pms-panel-header');
      if (chatHeader) {
        const closeBtn = chatHeader.querySelector('.pms-panel-close');
        if (closeBtn) {
          chatHeader.insertBefore(this.toggleButton, closeBtn);
        } else {
          chatHeader.appendChild(this.toggleButton);
        }
      }
    }
  }

  /**
   * Create minimize button for immersive mode
   */
  private createMinimizeButton(): void {
    if (!this.container) return;

    this.minimizeButton = document.createElement('button');
    this.minimizeButton.className = 'pms-minimize-btn';
    this.minimizeButton.title = 'Minimize';
    this.minimizeButton.innerHTML = `
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="18" height="18">
        <polyline points="4 14 10 14 10 20"/>
        <polyline points="20 10 14 10 14 4"/>
        <line x1="14" y1="10" x2="21" y2="3"/>
        <line x1="3" y1="21" x2="10" y2="14"/>
      </svg>
    `;

    this.minimizeButton.addEventListener('click', (e) => {
      e.stopPropagation(); // Prevent triggering resize
      this.minimize();
    });

    // In split immersive mode, add to top handle buttons container
    const topHandleButtons = this.topHandle?.querySelector('.pms-top-handle-buttons');
    if (topHandleButtons) {
      topHandleButtons.appendChild(this.minimizeButton);
    } else {
      // Fallback to panel header (non-split mode)
      const chatHeader = this.container.querySelector('.pms-panel-header');
      if (chatHeader) {
        const firstBtn = chatHeader.querySelector('button');
        if (firstBtn) {
          chatHeader.insertBefore(this.minimizeButton, firstBtn);
        } else {
          chatHeader.appendChild(this.minimizeButton);
        }
      }
    }
  }

  /**
   * Create menu button for chat actions (new conversation, clear)
   */
  private createMenuButton(): void {
    if (!this.container) return;

    this.menuButton = document.createElement('button');
    this.menuButton.className = 'pms-menu-btn';
    this.menuButton.title = 'Menu';
    this.menuButton.innerHTML = `
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="18" height="18">
        <circle cx="12" cy="12" r="1"/>
        <circle cx="12" cy="5" r="1"/>
        <circle cx="12" cy="19" r="1"/>
      </svg>
    `;

    this.menuButton.addEventListener('click', (e) => {
      e.stopPropagation();
      this.toggleMenu();
    });

    // Close menu on outside click (store ref for cleanup)
    this.boundMenuDocClickHandler = () => this.closeMenu();
    document.addEventListener('click', this.boundMenuDocClickHandler);

    // Add to top handle buttons container
    const topHandleButtons = this.topHandle?.querySelector('.pms-top-handle-buttons');
    if (topHandleButtons) {
      // Insert at the beginning (before toggle and minimize)
      topHandleButtons.insertBefore(this.menuButton, topHandleButtons.firstChild);
    }
  }

  /**
   * Toggle menu dropdown
   */
  private toggleMenu(): void {
    if (this.menuDropdown) {
      this.closeMenu();
      return;
    }

    this.menuDropdown = document.createElement('div');
    this.menuDropdown.className = 'pms-top-handle-dropdown';
    this.menuDropdown.innerHTML = `
      <button class="pms-top-handle-dropdown-item" data-action="new-chat">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16">
          <line x1="12" y1="5" x2="12" y2="19"/>
          <line x1="5" y1="12" x2="19" y2="12"/>
        </svg>
        New conversation
      </button>
      <button class="pms-top-handle-dropdown-item" data-action="clear">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16">
          <polyline points="3 6 5 6 21 6"/>
          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
        </svg>
        Clear chat
      </button>
    `;

    this.menuDropdown.addEventListener('click', (e) => {
      e.stopPropagation();
      const item = (e.target as HTMLElement).closest('.pms-top-handle-dropdown-item') as HTMLElement;
      if (item) {
        const action = item.dataset['action'];
        if (action === 'new-chat') this.chatPanel?.newSession();
        else if (action === 'clear') this.chatPanel?.clear();
        this.closeMenu();
      }
    });

    // Position below menu button
    const topHandleButtons = this.topHandle?.querySelector('.pms-top-handle-buttons');
    if (topHandleButtons) {
      topHandleButtons.appendChild(this.menuDropdown);
    }
  }

  /**
   * Close menu dropdown
   */
  private closeMenu(): void {
    this.menuDropdown?.remove();
    this.menuDropdown = null;
  }

  /**
   * Create layout toggle button (immersive ↔ vertical)
   */
  private createLayoutToggleButton(): void {
    if (!this.container) return;

    this.layoutToggleBtn = document.createElement('button');
    this.layoutToggleBtn.className = 'pms-menu-btn';
    this.layoutToggleBtn.type = 'button';
    this.layoutToggleBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.switchLayout();
    });

    this.activeLayout = (this.config.style === 'vertical') ? 'vertical' : 'immersive';
    this.updateLayoutToggleIcon();

    // If starting in vertical mode, set up vertical resize now (panel element exists at this point)
    if (this.activeLayout === 'vertical') {
      this.setupVerticalResize();
    }

    // Insert between menu and minimize in top handle buttons
    const topHandleButtons = this.topHandle?.querySelector('.pms-top-handle-buttons');
    if (topHandleButtons) {
      const minimizeBtn = topHandleButtons.querySelector('.pms-minimize-btn');
      if (minimizeBtn) {
        topHandleButtons.insertBefore(this.layoutToggleBtn, minimizeBtn);
      } else {
        topHandleButtons.appendChild(this.layoutToggleBtn);
      }
    }

    // Create floating layout toggle inside the panel (visible when nav covers top handle)
    this.createFloatingLayoutToggle();

    // Auto-switch to immersive on mobile
    const mql = window.matchMedia('(max-width: 600px)');
    const handleMobile = (e: MediaQueryListEvent | MediaQueryList) => {
      if (('matches' in e) && e.matches && this.activeLayout === 'vertical') {
        this.switchLayout();
      }
    };
    mql.addEventListener('change', handleMobile as (e: MediaQueryListEvent) => void);
    handleMobile(mql);
  }

  /**
   * Create bubble switch button (switch to bubble mode)
   */
  private createBubbleSwitchButton(): void {
    if (!this.container) return;

    const bubbleBtn = document.createElement('button');
    bubbleBtn.className = 'pms-menu-btn';
    bubbleBtn.type = 'button';
    bubbleBtn.title = 'Switch to bubble';
    bubbleBtn.setAttribute('aria-label', 'Switch to bubble chat');
    bubbleBtn.innerHTML = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="18" height="18">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
    </svg>`;
    bubbleBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.callbacks.onStyleSwitch?.('bubble');
    });

    // Insert between layout toggle and minimize in top handle buttons
    const topHandleButtons = this.topHandle?.querySelector('.pms-top-handle-buttons');
    if (topHandleButtons) {
      const minimizeBtn = topHandleButtons.querySelector('.pms-minimize-btn');
      if (minimizeBtn) {
        topHandleButtons.insertBefore(bubbleBtn, minimizeBtn);
      } else {
        topHandleButtons.appendChild(bubbleBtn);
      }
    }
  }

  /**
   * Create floating layout toggle inside the panel.
   * Visible in vertical mode only (when host nav may cover top handle buttons).
   */
  private createFloatingLayoutToggle(): void {
    if (!this.container) return;
    const panel = this.container.querySelector('.pms-panel') as HTMLElement | null;
    if (!panel) return;

    this.floatingLayoutToggle = document.createElement('button');
    this.floatingLayoutToggle.className = 'pms-floating-layout-toggle';
    this.floatingLayoutToggle.type = 'button';
    this.floatingLayoutToggle.addEventListener('click', (e) => {
      e.stopPropagation();
      this.switchLayout();
    });
    this.updateFloatingLayoutToggle();
    panel.appendChild(this.floatingLayoutToggle);
  }

  /**
   * Update floating layout toggle: visible in vertical, hidden in immersive.
   */
  private updateFloatingLayoutToggle(): void {
    if (!this.floatingLayoutToggle) return;
    if (this.activeLayout === 'vertical') {
      this.floatingLayoutToggle.style.display = 'flex';
      this.floatingLayoutToggle.title = 'Switch to bottom panel';
      this.floatingLayoutToggle.setAttribute('aria-label', 'Switch to bottom panel layout');
      this.floatingLayoutToggle.innerHTML = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16">
        <rect x="3" y="3" width="18" height="18" rx="2"/>
        <line x1="3" y1="15" x2="21" y2="15"/>
      </svg>`;
    } else {
      this.floatingLayoutToggle.style.display = 'none';
    }
  }

  /**
   * Switch between immersive and vertical layouts
   */
  private switchLayout(): void {
    if (!this.container) return;
    const panel = this.container.querySelector('.pms-panel') as HTMLElement | null;
    if (!panel) return;

    if (this.activeLayout === 'immersive') {
      // → vertical
      panel.classList.remove('pms-panel-immersive');
      panel.classList.add('pms-panel-vertical');
      this.container.classList.remove('pms-split-immersive');
      this.container.classList.add('pms-split-vertical');
      this.activeLayout = 'vertical';
      if (this.topHandle) this.topHandle.style.cursor = 'default';
      this.teardownTopHandleResize();
      // Clear immersive positioning
      this.container.style.removeProperty('--container-top');
      this.container.style.removeProperty('top');
      this.setupVerticalResize();
    } else {
      // → immersive
      panel.classList.remove('pms-panel-vertical');
      panel.classList.add('pms-panel-immersive');
      this.container.classList.remove('pms-split-vertical');
      this.container.classList.add('pms-split-immersive');
      this.activeLayout = 'immersive';
      if (this.topHandle) this.topHandle.style.cursor = 'ns-resize';
      this.teardownVerticalResize();
      panel.style.removeProperty('--panel-width');
      // Clear vertical width on container too
      this.container.style.removeProperty('--panel-width');
      // Restore immersive position
      this.containerTop = Math.round(window.innerHeight * 0.8);
      this.updateContainerTop();
      this.setupTopHandleResize();
    }
    this.updateLayoutToggleIcon();
    this.updateFloatingLayoutToggle();
    this.reapplyPush();
  }

  /**
   * Update the layout toggle button icon
   */
  private updateLayoutToggleIcon(): void {
    if (!this.layoutToggleBtn) return;
    if (this.activeLayout === 'immersive') {
      this.layoutToggleBtn.title = 'Switch to sidebar';
      this.layoutToggleBtn.setAttribute('aria-label', 'Switch to sidebar layout');
      this.layoutToggleBtn.innerHTML = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="18" height="18">
        <rect x="3" y="3" width="18" height="18" rx="2"/>
        <line x1="15" y1="3" x2="15" y2="21"/>
      </svg>`;
    } else {
      this.layoutToggleBtn.title = 'Switch to bottom panel';
      this.layoutToggleBtn.setAttribute('aria-label', 'Switch to bottom panel layout');
      this.layoutToggleBtn.innerHTML = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="18" height="18">
        <rect x="3" y="3" width="18" height="18" rx="2"/>
        <line x1="3" y1="15" x2="21" y2="15"/>
      </svg>`;
    }
  }

  /**
   * Setup left-edge resize handle for vertical mode
   */
  private setupVerticalResize(): void {
    if (!this.container) return;
    const target = this.container; // Resize the container itself for split-panel vertical

    this.verticalResizeHandle = document.createElement('div');
    this.verticalResizeHandle.className = 'pms-vertical-resize-handle';
    target.appendChild(this.verticalResizeHandle);

    const startResize = (clientX: number) => {
      this.isResizingLeft = true;
      this.resizeStartX = clientX;
      this.resizeStartWidth = target.getBoundingClientRect().width;
      target.classList.add('pms-resizing');
      document.body.style.cursor = 'ew-resize';
      document.body.style.userSelect = 'none';
    };

    this.boundLeftMouseMove = (e: MouseEvent) => {
      if (!this.isResizingLeft) return;
      e.preventDefault();
      const delta = this.resizeStartX - e.clientX;
      const maxWidth = window.innerWidth * 0.8;
      const newWidth = Math.max(340, Math.min(maxWidth, this.resizeStartWidth + delta));
      target.style.setProperty('--panel-width', `${newWidth}px`);
      this.pusher?.updateDuringDrag(newWidth);
    };

    this.boundLeftMouseUp = () => {
      this.isResizingLeft = false;
      target.classList.remove('pms-resizing');
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      this.pusher?.endDrag();
      if (this.boundLeftMouseMove) document.removeEventListener('mousemove', this.boundLeftMouseMove);
      if (this.boundLeftMouseUp) document.removeEventListener('mouseup', this.boundLeftMouseUp);
    };

    this.boundLeftTouchMove = (e: TouchEvent) => {
      if (!this.isResizingLeft || e.touches.length !== 1) return;
      e.preventDefault();
      const touch = e.touches[0]!;
      const delta = this.resizeStartX - touch.clientX;
      const maxWidth = window.innerWidth * 0.8;
      const newWidth = Math.max(340, Math.min(maxWidth, this.resizeStartWidth + delta));
      target.style.setProperty('--panel-width', `${newWidth}px`);
      this.pusher?.updateDuringDrag(newWidth);
    };

    this.boundLeftTouchEnd = () => {
      this.isResizingLeft = false;
      target.classList.remove('pms-resizing');
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      this.pusher?.endDrag();
      if (this.boundLeftTouchMove) document.removeEventListener('touchmove', this.boundLeftTouchMove);
      if (this.boundLeftTouchEnd) document.removeEventListener('touchend', this.boundLeftTouchEnd);
    };

    this.verticalResizeHandle.addEventListener('mousedown', (e) => {
      e.preventDefault();
      startResize(e.clientX);
      document.addEventListener('mousemove', this.boundLeftMouseMove!);
      document.addEventListener('mouseup', this.boundLeftMouseUp!);
    });

    this.verticalResizeHandle.addEventListener('touchstart', (e) => {
      if (e.touches.length !== 1) return;
      e.preventDefault();
      startResize(e.touches[0]!.clientX);
      document.addEventListener('touchmove', this.boundLeftTouchMove!, { passive: false });
      document.addEventListener('touchend', this.boundLeftTouchEnd!);
    }, { passive: false });
  }

  /**
   * Teardown vertical resize handle
   */
  private teardownVerticalResize(): void {
    this.verticalResizeHandle?.remove();
    this.verticalResizeHandle = null;
    if (this.boundLeftMouseMove) document.removeEventListener('mousemove', this.boundLeftMouseMove);
    if (this.boundLeftMouseUp) document.removeEventListener('mouseup', this.boundLeftMouseUp);
    if (this.boundLeftTouchMove) document.removeEventListener('touchmove', this.boundLeftTouchMove);
    if (this.boundLeftTouchEnd) document.removeEventListener('touchend', this.boundLeftTouchEnd);
  }

  /**
   * Teardown top handle resize listeners
   */
  private teardownTopHandleResize(): void {
    if (this.boundTopMouseMove) document.removeEventListener('mousemove', this.boundTopMouseMove);
    if (this.boundTopMouseUp) document.removeEventListener('mouseup', this.boundTopMouseUp);
    if (this.boundTopTouchMove) document.removeEventListener('touchmove', this.boundTopTouchMove);
    if (this.boundTopTouchEnd) document.removeEventListener('touchend', this.boundTopTouchEnd);
    if (this.topHandle) {
      this.topHandle.onmousedown = null;
      this.topHandle.ontouchstart = null;
    }
  }

  /**
   * Create floating bubble for minimized state
   */
  private createFloatingBubble(): void {
    if (!this.wrapper) return;

    const position = this.config.position ?? 'bottom-right';
    this.floatingBubble = document.createElement('button');
    this.floatingBubble.className = `pms-floating-bubble pms-bubble pms-bubble-${position}`;
    this.floatingBubble.setAttribute('aria-label', 'Open Chat');
    this.floatingBubble.setAttribute('type', 'button');

    this.floatingBubble.innerHTML = `
      <span class="pms-bubble-icon pms-bubble-icon-chat">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
        </svg>
      </span>
    `;

    this.floatingBubble.addEventListener('click', () => {
      this.restore();
    });

    // Add to wrapper (not container, so it's visible when container is hidden)
    this.wrapper.appendChild(this.floatingBubble);
  }

  /**
   * Minimize the panel to floating bubble
   */
  minimize(): void {
    this.isMinimized = true;
    this.wrapper?.classList.add('pms-minimized');
    this.container?.classList.add('pms-hidden');
    this.floatingBubble?.classList.add('pms-visible');
    this.pusher?.reset();
  }

  /**
   * Restore from minimized state
   */
  restore(): void {
    this.isMinimized = false;
    this.wrapper?.classList.remove('pms-minimized');
    this.container?.classList.remove('pms-hidden');
    this.floatingBubble?.classList.remove('pms-visible');
    this.reapplyPush();
  }

  /** Re-apply push based on current layout and panel/container dimensions. */
  private reapplyPush(): void {
    if (!this.pusher || !this.container) return;
    if (this.activeLayout === 'vertical') {
      this.pusher.pushRight(this.container.getBoundingClientRect().width);
    } else if (this.activeLayout === 'immersive') {
      this.pusher.pushBottom(window.innerHeight - this.containerTop);
    }
  }

  /**
   * Check if panel is minimized
   */
  isMinimizedState(): boolean {
    return this.isMinimized;
  }

  /**
   * Subscribe to list store changes for auto-open and badge updates (disabled — feature not ready)
   */
  // @ts-ignore: Kept for re-enablement when list sidebar is ready
  private subscribeToListChanges(): void {
    const listStore = getListStore();

    this.unsubscribeFromList = listStore.on('change', (items) => {
      this.updateToggleBadge(items.length);

      // Auto-open summary panel on first item
      if (this.splitConfig.autoOpen && items.length === 1 && !this.isSummaryOpen) {
        this.openSummary();
      }
    });
  }

  /**
   * Update the toggle button badge count
   */
  private updateToggleBadge(count: number): void {
    const badge = this.toggleButton?.querySelector('.pms-split-toggle-badge');
    if (badge) {
      badge.textContent = String(count);
      badge.classList.toggle('pms-visible', count > 0);
    }
  }

  /**
   * Handle action from chat panel (including list:add, list:remove)
   */
  private handleChatAction(action: string): void {
    // Guard against undefined action
    if (!action) return;

    // Flow actions are handled directly by ChatPanel
    if (action.startsWith('flow:')) {
      return;
    }

    // Handle list actions
    if (action.startsWith('list:')) {
      const parts = action.split(':');
      const listAction = parts[1];
      // itemId is parts[2] if needed in future

      if (listAction === 'add' && !this.isSummaryOpen && this.splitConfig.autoOpen) {
        this.openSummary();
      }

      // listStore is already updated by the card click handler
      return;
    }

    // Handle cart actions (from browse "Add to Cart" button)
    if (action.startsWith('cart:')) {
      const parts = action.split(':');
      const cartAction = parts[1];

      if (cartAction === 'add' && !this.isSummaryOpen && this.splitConfig.autoOpen) {
        this.openSummary();
      }

      // listStore is already updated by the browse card click handler
      return;
    }
  }

  /**
   * Handle action from summary panel - context-aware actions
   */
  private handleSummaryAction(action: string, items: ListItem[]): void {
    // Notify callback
    this.callbacks.onSummaryAction?.(action, items);

    const titles = items.map(i => i.title).join(', ');
    const count = items.length;

    // Handle known actions based on context
    switch (action) {
      // Comparison actions
      case 'compare':
      case 'compare_items':
      case 'compare_side_by_side':
        this.sendMessage(`Compare these ${count} items side by side: ${titles}`);
        break;

      case 'compare_prices':
        this.sendMessage(`Compare the prices of: ${titles}`);
        break;

      case 'compare_specs':
      case 'compare_players':
      case 'compare_properties':
        this.sendMessage(`Compare these in detail: ${titles}`);
        break;

      // Shopping actions
      case 'checkout': {
        const api = (window as unknown as Record<string, unknown>).PromptMySite as
          { onCheckout?: ((items: ListItem[]) => void | Promise<void | CartResult>) | null } | undefined;
        const externalHandler = api?.onCheckout;
        if (externalHandler || detectPlatform() !== 'unknown') {
          this.handleExternalCheckout(items, externalHandler ?? null);
        } else {
          this.sendMessage('I\'d like to proceed with checkout for my selected items');
        }
        break;
      }

      case 'get_quote':
        this.sendMessage(`Get me a quote for: ${titles}`);
        break;

      // Build/gaming actions
      case 'build_summary':
        this.sendMessage(`Summarize my current build: ${titles}`);
        break;

      case 'analyze_synergies':
        this.sendMessage(`Analyze the synergies between these items in my build: ${titles}`);
        break;

      case 'export_build':
        this.sendMessage(`Export my build configuration: ${titles}`);
        break;

      case 'search_alternatives':
        this.sendMessage(`Find alternatives for the items in my build`);
        break;

      // Booking actions
      case 'book_test_drive':
        this.sendMessage(`I'd like to book test drives for: ${titles}`);
        break;

      case 'finance_quote':
        this.sendMessage(`Get finance quotes for: ${titles}`);
        break;

      case 'schedule_viewings':
        this.sendMessage(`Schedule viewings for these properties: ${titles}`);
        break;

      case 'request_valuations':
        this.sendMessage(`Request valuations for: ${titles}`);
        break;

      case 'book_services':
        this.sendMessage(`Book these services: ${titles}`);
        break;

      // Recruitment actions
      case 'contact_agents':
        this.sendMessage(`Help me contact the agents for these players: ${titles}`);
        break;

      case 'export_shortlist':
        this.sendMessage(`Export my shortlist: ${titles}`);
        break;

      // Research/search actions
      case 'search_similar':
      case 'find_best':
        this.sendMessage(`Find more items similar to: ${titles}`);
        break;

      // Dynamic search actions (search_more_<category>)
      default:
        if (action.startsWith('search_more_')) {
          const category = action.replace('search_more_', '');
          this.sendMessage(`Show me more ${category}`);
        } else if (action.startsWith('browse_')) {
          const category = action.replace('browse_', '');
          this.sendMessage(`Browse more ${category}`);
        }
        // Other custom actions - just trigger the callback
        break;
    }
  }

  /**
   * Handle external checkout — callback or auto-detected platform.
   * Shows loading/success/error states on the checkout button.
   */
  private async handleExternalCheckout(
    items: ListItem[],
    handler: ((items: ListItem[]) => void | Promise<void | CartResult>) | null
  ): Promise<void> {
    // Find the checkout button in the summary panel
    const btn = this.summaryContainer?.querySelector('[data-action="checkout"]') as HTMLButtonElement | null;
    const originalText = btn?.textContent ?? 'Checkout';

    // Loading state
    if (btn) {
      btn.disabled = true;
      btn.classList.add('pms-summary-action-loading');
      btn.textContent = 'Adding...';
    }

    try {
      let result: CartResult | void | null = null;

      if (handler) {
        // Use registered callback
        result = await Promise.resolve(handler(items));
      } else {
        // Use auto-detected platform
        result = await addToCart(items);
      }

      // Success state
      if (btn) {
        btn.classList.remove('pms-summary-action-loading');
        btn.classList.add('pms-summary-action-success');
        btn.textContent = 'Added!';
      }

      // Clear list if requested
      if (result && typeof result === 'object' && result.clear) {
        setTimeout(() => {
          const listStore = getListStore();
          listStore.clear();
        }, 800);
      }

      // Reset button after delay
      setTimeout(() => {
        if (btn) {
          btn.disabled = false;
          btn.classList.remove('pms-summary-action-success');
          btn.textContent = originalText;
        }
      }, 2000);

    } catch (err) {
      warn('Checkout failed:', err);

      // Error state
      if (btn) {
        btn.classList.remove('pms-summary-action-loading');
        btn.classList.add('pms-summary-action-error');
        btn.textContent = 'Failed';
      }

      // Reset button after delay
      setTimeout(() => {
        if (btn) {
          btn.disabled = false;
          btn.classList.remove('pms-summary-action-error');
          btn.textContent = originalText;
        }
      }, 2000);
    }
  }

  /**
   * Send a message via the chat panel
   */
  private sendMessage(message: string): void {
    // Get the input element and trigger send
    const input = this.container?.querySelector('.pms-input') as HTMLInputElement;
    if (input) {
      input.value = message;
      input.dispatchEvent(new KeyboardEvent('keypress', { key: 'Enter' }));
    }
  }

  /**
   * Open the summary panel
   */
  openSummary(): void {
    if (!this.summaryContainer) return;

    this.isSummaryOpen = true;
    this.container?.classList.add('pms-split-open');
    this.summaryPanel?.open();
  }

  /**
   * Close the summary panel
   */
  closeSummary(): void {
    if (!this.summaryContainer) return;

    this.isSummaryOpen = false;
    this.container?.classList.remove('pms-split-open');
  }

  /**
   * Enter bubble mode: move panel out of split wrapper for proper bubble positioning.
   * Hides the split wrapper (top handle, summary) and reparents the panel element.
   */
  enterBubbleMode(): void {
    this.closeSummary();

    // Move the panel element out of split wrapper → direct child of innerContainer
    const chatWrapper = this.container?.querySelector('.pms-split-chat');
    const panelEl = chatWrapper?.querySelector('.pms-panel') as HTMLElement | null;
    if (panelEl && this.wrapper?.parentElement) {
      this.wrapper.parentElement.appendChild(panelEl);
    }

    // Hide the entire split wrapper (top handle, container, summary)
    if (this.wrapper) this.wrapper.style.display = 'none';
  }

  /**
   * Exit bubble mode: restore the panel back into the split wrapper.
   */
  exitBubbleMode(): void {
    // Show the split wrapper
    if (this.wrapper) this.wrapper.style.display = '';

    // Move the panel element back into the split chat wrapper
    const chatWrapper = this.container?.querySelector('.pms-split-chat');
    const panelEl = this.wrapper?.parentElement?.querySelector(':scope > .pms-panel') as HTMLElement | null;
    if (panelEl && chatWrapper) {
      chatWrapper.appendChild(panelEl);
    }

    // Ensure container position is set (may not have been called if started in bubble)
    this.updateContainerTop();
  }

  /**
   * Toggle summary panel visibility
   */
  toggleSummary(): void {
    if (this.isSummaryOpen) {
      this.closeSummary();
    } else {
      this.openSummary();
    }
  }

  /**
   * Check if summary panel is open
   */
  isSummaryVisible(): boolean {
    return this.isSummaryOpen;
  }

  /**
   * Set the host-page pusher for push-content feature.
   */
  setPusher(pusher: HostPagePusher): void {
    this.pusher = pusher;
  }

  /**
   * Set the shadow root for modals
   */
  setShadowRoot(shadowRoot: ShadowRoot): void {
    this.chatPanel?.setShadowRoot(shadowRoot);
  }

  /**
   * Show the container
   */
  show(): void {
    this.chatPanel?.show();
    this.container?.classList.add('pms-visible');
  }

  /**
   * Hide the container
   */
  hide(): void {
    this.chatPanel?.hide();
    this.container?.classList.remove('pms-visible');
  }

  /**
   * Toggle visibility
   */
  toggle(): void {
    if (this.chatPanel?.isOpen()) {
      this.hide();
    } else {
      this.show();
    }
  }

  /**
   * Check if the container is visible
   */
  isOpen(): boolean {
    return this.chatPanel?.isOpen() ?? false;
  }

  /**
   * Clear chat history (keeps list)
   */
  clear(): void {
    this.chatPanel?.clear();
  }

  /**
   * Start a new session (clears chat and list)
   */
  newSession(): void {
    this.chatPanel?.newSession();
    resetListStore();
  }

  /**
   * Set panel title
   */
  setTitle(title: string): void {
    this.chatPanel?.setTitle(title);
  }

  /**
   * Get the underlying chat panel
   */
  getChatPanel(): ChatPanel | null {
    return this.chatPanel;
  }

  /**
   * Get the summary panel
   */
  getSummaryPanel(): SummaryPanel | null {
    return this.summaryPanel;
  }

  /**
   * Destroy the container and clean up
   */
  destroy(): void {
    // Save panel position to sessionStorage (preserves position across workspace switches)
    if (this.containerTop > 0) {
      sessionStorage.setItem('pms-panel-top', String(this.containerTop));
    }

    if (this.unsubscribeFromList) {
      this.unsubscribeFromList();
      this.unsubscribeFromList = null;
    }

    // Clean up document-level menu click listener
    if (this.boundMenuDocClickHandler) {
      document.removeEventListener('click', this.boundMenuDocClickHandler);
      this.boundMenuDocClickHandler = null;
    }

    // Clean up top handle event listeners
    if (this.boundTopMouseMove) {
      document.removeEventListener('mousemove', this.boundTopMouseMove);
      this.boundTopMouseMove = null;
    }
    if (this.boundTopMouseUp) {
      document.removeEventListener('mouseup', this.boundTopMouseUp);
      this.boundTopMouseUp = null;
    }
    if (this.boundTopTouchMove) {
      document.removeEventListener('touchmove', this.boundTopTouchMove);
      this.boundTopTouchMove = null;
    }
    if (this.boundTopTouchEnd) {
      document.removeEventListener('touchend', this.boundTopTouchEnd);
      document.removeEventListener('touchcancel', this.boundTopTouchEnd);
      this.boundTopTouchEnd = null;
    }

    this.topHandle?.remove();
    this.topHandle = null;

    this.minimizeButton?.remove();
    this.minimizeButton = null;

    this.floatingBubble?.remove();
    this.floatingBubble = null;

    this.floatingLayoutToggle?.remove();
    this.floatingLayoutToggle = null;

    this.summaryPanel?.destroy();
    this.chatPanel?.destroy();

    this.container?.remove();
    this.container = null;

    this.wrapper?.remove();
    this.wrapper = null;

    this.chatPanel = null;
    this.summaryPanel = null;
    this.summaryContainer = null;
    this.toggleButton = null;
  }
}
